import type PhotoSwipeLightbox from 'photoswipe/lightbox'
import type PhotoSwipe from 'photoswipe'

type UseLightboxOptions = {
  /**
   * Called when the slide index in the lightbox changes.
   */
  onSlideChange?: (index: number) => void
}

/**
 * Composable to interact with the PhotoSwipe library.
 */
type UseLightbox = {
  /**
   * Whether the lightbox is currently open.
   */
  isOpen: ComputedRef<boolean>
}

export async function useLightbox(
  /**
   * The element containing the images to open in the lightbox.
   */
  el: Ref<HTMLElement | null | undefined>,

  /**
   * Options.
   */
  options?: UseLightboxOptions,
): Promise<UseLightbox> {
  // The lightbox instance.
  let lightbox: PhotoSwipeLightbox | null = null
  let initialised = false
  const isOpen = ref(false)

  onMounted(initPhotoswipe)

  onBeforeUnmount(() => {
    if (lightbox) {
      lightbox.destroy()
    }
  })

  watch(el, initPhotoswipe)

  // Import the CSS.
  await import('~/assets/css/dynamic/photoswipe.css')

  // Load the SVGs.
  const { default: arrowPrevSVG } =
    await import('~/assets/symbols/chevron-left.svg?raw')
  const { default: arrowNextSVG } =
    await import('~/assets/symbols/chevron-right.svg?raw')
  const { default: closeSVG } = await import('~/assets/symbols/close.svg?raw')

  // Load the library.
  const PhotoSwipeLightbox = await import('photoswipe/lightbox').then(
    (v) => v.default,
  )

  function initPhotoswipe() {
    if (!el.value || initialised) {
      return
    }

    initialised = true

    lightbox = new PhotoSwipeLightbox({
      arrowPrevSVG,
      arrowNextSVG,
      closeSVG,
      gallery: el.value,
      children: 'a',
      pswpModule: () => import('photoswipe'),
      showHideAnimationType: 'fade',
      bgOpacity: 1,
    })

    // Prevent the glitchy behaviour in the open transition.
    lightbox.addFilter('useContentPlaceholder', () => {
      return false
    })

    lightbox.on('beforeOpen', () => {
      isOpen.value = true
    })

    lightbox.on('close', () => {
      isOpen.value = false
    })

    lightbox.on('change', function () {
      if (window.pswp && options?.onSlideChange) {
        options.onSlideChange(window.pswp.currIndex)
      }
    })

    lightbox.on('uiElementCreate', function (ctx) {
      if (ctx.data.name === 'counter') {
        // Move the counter element to the root.
        ctx.data.appendTo = 'root'
      }
    })

    lightbox.on('uiRegister', function () {
      if (!lightbox?.pswp?.ui) {
        return
      }

      const pswp = lightbox.pswp

      lightbox.pswp.ui.registerElement({
        name: 'custom-caption',
        order: 9,
        isButton: false,
        appendTo: 'root',
        html: '',
        onInit: (el) => {
          pswp.on('change', () => {
            const currSlideElement = pswp.currSlide?.data.element
            let captionHTML: string | null | undefined = ''
            if (currSlideElement) {
              captionHTML = currSlideElement.dataset.title
            }
            el.innerHTML = captionHTML || ''
          })
        },
      })
    })

    lightbox.init()
  }

  return {
    isOpen: computed(() => isOpen.value),
  }
}

declare global {
  interface Window {
    /**
     * The globally injected PhotoSwipe instance.
     */
    pswp?: PhotoSwipe
  }
}
